import {
  AfterViewInit,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  EventEmitter,
  Inject,
  Injectable,
  Input,
  OnChanges,
  OnDestroy,
  Output,
  SimpleChanges,
  ViewChild,
} from '@angular/core';
import {
  AbstractControl,
  FormsModule,
  ReactiveFormsModule,
  UntypedFormControl,
  UntypedFormGroup,
  ValidationErrors,
  ValidatorFn,
} from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import {
  DateAdapter,
  MAT_DATE_FORMATS,
  MAT_DATE_LOCALE,
  MatDateFormats,
  MatNativeDateModule,
  NativeDateAdapter,
} from '@angular/material/core';
import {
  DefaultMatCalendarRangeStrategy,
  MAT_DATE_RANGE_SELECTION_STRATEGY,
  MatCalendar,
  MatDatepickerModule,
} from '@angular/material/datepicker';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { I18NextModule } from 'angular-i18next';
import { Subject, takeUntil } from 'rxjs';
import { IconComponent } from 'src/app/common/icon/icon.component';
import { DateRange, getTimeOptions, SHARRY_DATE_FORMATS } from 'src/app/common/utils/helpers';
import { datesAreOnSameDay } from 'utils/date-utils';
import { SharryTimeInput, TimeInputComponent } from '../time-input/time-input.component';

@Injectable()
export class SharryDateAdapter extends NativeDateAdapter {
  parse(value: any): Date | null {
    if (typeof value === 'string' && value.indexOf('/') > -1) {
      const str = value.split('/');
      if (str.length < 2 || isNaN(+str[0]) || isNaN(+str[1]) || isNaN(+str[2])) {
        return null;
      }
      return new Date(Number(str[2]), Number(str[1]) - 1, Number(str[0]), 12, 0, 0, 0);
    }
    const timestamp = typeof value === 'number' ? value : Date.parse(value);
    return isNaN(timestamp) ? null : new Date(timestamp);
  }

  format(date: Date, displayFormat: Intl.DateTimeFormatOptions | 'input'): string {
    if (displayFormat === 'input') {
      const day = date.getDate().toLocaleString('it-IT', { minimumIntegerDigits: 2, useGrouping: false });
      const month = (date.getMonth() + 1).toLocaleString('it-IT', { minimumIntegerDigits: 2, useGrouping: false });
      const year = date.getFullYear().toLocaleString('it-IT', { minimumIntegerDigits: 4, useGrouping: false });
      // Return the format as per your requirement
      return `${day}/${month}/${year}`;
    } else {
      return date.toLocaleDateString('it-IT', displayFormat);
    }
  }

  getFirstDayOfWeek(): number {
    return 1;
  }
}

@Component({
  selector: 'sharry-range-calendar',
  standalone: true,
  imports: [
    MatDatepickerModule,
    MatNativeDateModule,
    I18NextModule,
    TimeInputComponent,
    FormsModule,
    ReactiveFormsModule,
    MatFormFieldModule,
    MatInputModule,
    IconComponent,
    MatButtonModule,
  ],
  templateUrl: './range-calendar.component.html',
  styleUrls: ['./range-calendar.component.scss'],
  providers: [
    {
      provide: MAT_DATE_LOCALE,
      useValue: 'it-IT',
    },
    { provide: DateAdapter, useClass: SharryDateAdapter },
    {
      provide: MAT_DATE_FORMATS,
      useValue: SHARRY_DATE_FORMATS,
    },
    {
      provide: MAT_DATE_RANGE_SELECTION_STRATEGY,
      useClass: DefaultMatCalendarRangeStrategy,
    },
  ],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class RangeCalendarComponent implements OnChanges, AfterViewInit {
  /** start date - end date */
  @Input() dateRange: DateRange<Date>;
  /** @optional start time - end time */
  @Input() timeRange: DateRange<SharryTimeInput>;
  /** @optional min available date */
  @Input() minDate: Date;
  /** @optional max available date */
  @Input() maxDate: Date;
  /** @optional A date representing the period (month or year) to start the calendar in */
  @Input() startAt: Date;
  /** @optional if true enable ending time */
  @Input() isExtendedTime: boolean;
  /** @optional if true enable ending date */
  @Input() isExtendedDate = true;
  /** @optional enable/disable time range, default true */
  @Input() isTime = true;
  /** @optional enable/disable date range, default true */
  @Input() isCalendar = true;

  @Input() dateFilter: (date: Date) => boolean;
  @Input() dateTitle: string;
  @Input() timeTitle: string;
  @Input() isInModal = false;
  @Input() readOnly = false;

  @Output() selectedChange = new EventEmitter<DateRange<Date>>();
  @Output() timeStartChange = new EventEmitter<SharryTimeInput>();
  @Output() timeEndChange = new EventEmitter<SharryTimeInput>();

  @ViewChild('calendar') calendar: MatCalendar<Date>;

  range: UntypedFormGroup;
  singleStartControl: UntypedFormControl;
  focused = false;
  minTimeStart: { hours: number; minutes: number } = { hours: 0, minutes: 0 };
  startOptions: Map<string, string[]> = getTimeOptions();
  minTimeEnd: { hours: number; minutes: number } = { hours: 0, minutes: 0 };
  endOptions: Map<string, string[]> = getTimeOptions();
  exampleHeader = ExampleHeaderComponent;

  constructor() {
    this.range = new UntypedFormGroup(
      {
        start: new UntypedFormControl(),
        end: new UntypedFormControl(),
      },
      { validators: this.validateDateRange },
    );
    this.singleStartControl = new UntypedFormControl('', { validators: this.validateDateRangeSingle });
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes) {
      if (changes.timeRange) {
        if (!changes.timeRange.currentValue) {
          this.timeRange = new DateRange({ hours: '08', minutes: '00' }, { hours: '08', minutes: '45' });
        } else {
          this.timeRange = new DateRange(
            {
              hours: changes.timeRange.currentValue.start?.hours || '08',
              minutes: changes.timeRange.currentValue.start?.minutes || '00',
            },
            {
              hours: changes.timeRange.currentValue.end?.hours || '08',
              minutes: changes.timeRange.currentValue.end?.minutes || '45',
            },
          );
        }
        this.calcMinTime();
        this.manageTime();
      }
      if (changes.dateRange) {
        if (changes.dateRange.currentValue) {
          this.range.get('start').setValue(changes.dateRange.currentValue.start);
          this.range.get('end').setValue(changes.dateRange.currentValue.end);
          this.singleStartControl.setValue(changes.dateRange.currentValue.start);
          this.calcMinTime();
          this.manageTime();
        } else {
          this.range.get('start').setValue(undefined);
          this.range.get('end').setValue(undefined);
          this.singleStartControl.setValue(undefined);
        }
      }
      if (changes.readOnly) {
        if (changes.readOnly.currentValue) {
          this.range.disable();
          this.range.updateValueAndValidity();
          this.singleStartControl.disable();
          this.singleStartControl.updateValueAndValidity();
        } else {
          this.range.enable();
          this.range.updateValueAndValidity();
          this.singleStartControl.enable();
          this.singleStartControl.updateValueAndValidity();
        }
      }
    }
  }

  ngAfterViewInit(): void {
    if (!!this.calendar && !!this.dateRange?.start) {
      this.calendar._yearSelectedInMultiYearView(this.dateRange?.start);
      this.calendar._monthSelectedInYearView(this.dateRange?.start);
    }
  }

  onSelectedDateChange(date: Date, isRange: boolean): void {
    if (this.dateRange && this.dateRange.start && date > this.dateRange.end && isRange) {
      if (!!this.maxDate && date > this.maxDate) {
        this.setCompleteRange(date, date);
      } else {
        this.setCompleteRange(this.dateRange.start, date);
      }
    } else {
      if (!!this.timeRange && !!this.timeRange.start && !!this.timeRange.end) {
        this.calcMinTime();
        this.manageTime();
      }
      this.setCompleteRange(date, date);
    }
    if (isRange) {
      this.range.get('start').setValue(this.dateRange.start);
      this.range.get('end').setValue(this.dateRange.end);
    } else {
      this.singleStartControl.setValue(this.dateRange.start);
    }
    this.selectedChange.emit(this.dateRange);
  }

  onFocusChange(focus: boolean) {
    Promise.resolve(null).then(() => (this.focused = focus));
  }

  validateDateRange: ValidatorFn = (control: AbstractControl): ValidationErrors => {
    if (control) {
      const fromDate: Date = control.value.start;
      const toDate: Date = control.value.end;
      if (fromDate && toDate) {
        if (fromDate.getTime() > toDate.getTime()) {
          return {
            rangeExceeded: true,
          };
        }
        if (fromDate.getTime() < (this.minDate || new Date()).getTime()) {
          return {
            rangeExceeded: true,
          };
        }
        if (toDate.getTime() < (this.minDate || new Date()).getTime()) {
          return {
            rangeExceeded: true,
          };
        }
        return null;
      } else {
        return {
          rangeExceeded: true,
        };
      }
    }
    return null;
  };
  validateDateRangeSingle: ValidatorFn = (control: AbstractControl): ValidationErrors => {
    if (control) {
      const fromDate: Date = control.value;
      if (fromDate) {
        if (fromDate.getTime() < (this.minDate || new Date()).getTime()) {
          return {
            rangeExceeded: true,
          };
        }
        return null;
      } else {
        return {
          rangeExceeded: true,
        };
      }
    }
    return null;
  };

  calcMinTime() {
    if (!!this.dateRange && !!this.dateRange.start) {
      if (datesAreOnSameDay(new Date(), this.dateRange.start)) {
        const minutes = new Date().getMinutes();
        const dateStart = new Date(new Date().setMinutes(Math.ceil(minutes / 15) * 15));
        this.minTimeStart = { hours: dateStart.getHours(), minutes: dateStart.getMinutes() };
        const tH = this.minTimeStart.hours;
        const tm = this.minTimeStart.minutes;
        this.startOptions = getTimeOptions();
        for (let i = 0; i < tH; i++) {
          this.startOptions.delete(i.toLocaleString(undefined, { minimumIntegerDigits: 2 }));
        }
        const minutesArr = ['00', '15', '30', '45'];
        for (let i = 0; i < tm / 15; i++) {
          minutesArr.splice(0, 1);
        }
        this.startOptions.set(tH.toLocaleString(undefined, { minimumIntegerDigits: 2 }), minutesArr);
      } else {
        this.minTimeStart = { hours: 0, minutes: 0 };
        this.startOptions = getTimeOptions();
      }
      if (!!this.dateRange.end && datesAreOnSameDay(this.dateRange.start, this.dateRange.end)) {
        const startGt = Number(this.timeRange?.start?.hours || 0) > (this.minTimeStart.hours || 0);
        const hours = startGt ? Number(this.timeRange?.start?.hours || 0) : this.minTimeStart.hours || 0;
        const minutes = startGt ? Number(this.timeRange?.start?.minutes || 0) : this.minTimeStart.minutes || 0;
        const date = new Date(new Date().setHours(hours, minutes + 15));
        this.minTimeEnd = { hours: date.getHours(), minutes: date.getMinutes() };
        const tH = this.minTimeEnd.hours;
        const tm = this.minTimeEnd.minutes;
        this.endOptions = getTimeOptions();
        for (let i = 0; i < tH; i++) {
          this.endOptions.delete(i.toLocaleString(undefined, { minimumIntegerDigits: 2 }));
        }
        const minutesArr = ['00', '15', '30', '45'];
        for (let i = 0; i < tm / 15; i++) {
          minutesArr.splice(0, 1);
        }
        this.endOptions.set(tH.toLocaleString(undefined, { minimumIntegerDigits: 2 }), minutesArr);
      } else {
        this.minTimeEnd = { hours: 0, minutes: 0 };
        this.endOptions = getTimeOptions();
      }
    }
  }

  onTimeInputChange(timeFieldName: 'start' | 'end', time: SharryTimeInput) {
    if (this.dateRange) {
      this.calcMinTime();
      if (timeFieldName === 'start') {
        const startH = Number(time?.hours || 0);
        const startM = Number(time?.minutes || 0);
        const startTime = {
          hours: (startH < this.minTimeStart.hours ? this.minTimeStart.hours : startH).toLocaleString(undefined, {
            minimumIntegerDigits: 2,
          }),
          minutes: (startM < this.minTimeStart.minutes ? this.minTimeStart.minutes : startM).toLocaleString(undefined, {
            minimumIntegerDigits: 2,
          }),
        };
        this.timeRange.start = startTime;
        if (this.isExtendedTime) {
          this.onTimeInputChange('end', this.timeRange.end);
        }
        this.timeStartChange.emit(startTime);
      }
      if (timeFieldName === 'end') {
        const endH = Number(time?.hours || 0);
        const endM = Number(time?.minutes || 0);
        const endTime = {
          hours: (endH < this.minTimeEnd.hours ? this.minTimeEnd.hours : endH).toLocaleString(undefined, {
            minimumIntegerDigits: 2,
          }),
          minutes: (endH === this.minTimeEnd.hours && endM < this.minTimeEnd.minutes
            ? this.minTimeEnd.minutes
            : endM
          ).toLocaleString(undefined, { minimumIntegerDigits: 2 }),
        };
        this.timeRange.end = endTime;
        this.timeEndChange.emit(endTime);
      }
      this.setCompleteRange(this.dateRange.start, this.dateRange.end);
      this.selectedChange.emit(this.dateRange);
    }
  }

  onRangeChange(calendar: MatCalendar<Date>, isRange: boolean) {
    let value = isRange ? this.range.value : this.singleStartControl.value;
    if (!!calendar && !!value && isRange) {
      if (!!value.start && value.start.getTime() < this.minDate.getTime()) {
        this.range.get('start').setValue(this.minDate);
        value.start = this.minDate;
      }
      if (!!value.end && value.end.getTime() < this.minDate.getTime()) {
        this.range.get('end').setValue(this.minDate);
        value.end = this.minDate;
      } else if (!!value.end && !!this.maxDate && value.end.getTime() >= this.maxDate.getTime()) {
        this.range.get('start').setValue(value.end);
        value.start = value.end;
        this.range.get('end').setValue(value.end);
      }
      if (!!value.end && value.end.getTime() < value.start.getTime()) {
        this.range.get('end').setValue(value.start);
        value.end = value.start;
      }
      if (value.end) {
        if (
          !this.dateRange ||
          value.end.getTime() !== (this.dateRange || new DateRange(new Date(), new Date())).end.getTime() ||
          value.start.getTime() !== (this.dateRange || new DateRange(new Date(), new Date())).start.getTime()
        ) {
          this.dateRange = new DateRange(value.start, value.end);
          calendar._yearSelectedInMultiYearView(value.end);
          calendar._monthSelectedInYearView(value.end);
          if (this.range.valid) {
            this.selectedChange.emit(this.dateRange);
          }
        }
      } else if (value.start) {
        if (
          !this.dateRange ||
          value.start.getTime() !== (this.dateRange || new DateRange(new Date(), new Date())).start.getTime()
        ) {
          this.range.get('end').setValue(value.start);
          this.dateRange = new DateRange(value.start, value.start);
          calendar._yearSelectedInMultiYearView(value.start);
          calendar._monthSelectedInYearView(value.start);
          if (this.range.valid) {
            this.selectedChange.emit(this.dateRange);
          }
        }
      }
    } else if (!!calendar && !!value && !isRange) {
      if (value.getTime() < this.minDate.getTime()) {
        this.singleStartControl.setValue(this.minDate);
        value = this.minDate;
      }
      if (
        !this.dateRange ||
        value.getTime() !== (this.dateRange || new DateRange(new Date(), new Date())).start.getTime()
      ) {
        this.singleStartControl.setValue(value);
        this.dateRange = new DateRange(value, value);
        calendar._yearSelectedInMultiYearView(value);
        calendar._monthSelectedInYearView(value);
        if (this.singleStartControl.valid) {
          this.selectedChange.emit(this.dateRange);
        }
      }
    }
    this.calcMinTime();
    this.manageTime();
  }

  setCompleteRange(startDate: Date, endDate: Date) {
    const startHours = Number(this.isTime ? this.timeRange?.start?.hours || '23' : '23');
    const startMinutes = Number(this.isTime ? this.timeRange?.start?.minutes || '59' : '59');
    const endHours = Number(this.isTime ? this.timeRange?.end?.hours || '23' : '23');
    const endMinutes = Number(this.isTime ? this.timeRange?.end?.minutes || '59' : '59');
    this.dateRange = new DateRange(
      new Date(startDate.setHours(startHours, startMinutes, 0, 0)),
      new Date(endDate.setHours(endHours, endMinutes, 1, 0)),
    );
  }

  manageTime() {
    const startH = Number(this.timeRange?.start?.hours || 0);
    const startM = Number(this.timeRange?.start?.minutes || 0);
    const endH = Number(this.timeRange?.end?.hours || 0);
    const endM = Number(this.timeRange?.end?.minutes || 0);
    this.timeRange = new DateRange(
      {
        hours: (startH < this.minTimeStart.hours ? this.minTimeStart.hours : startH).toLocaleString(undefined, {
          minimumIntegerDigits: 2,
        }),
        minutes: (startM < this.minTimeStart.minutes ? this.minTimeStart.minutes : startM).toLocaleString(undefined, {
          minimumIntegerDigits: 2,
        }),
      },
      {
        hours: (endH < this.minTimeEnd.hours ? this.minTimeEnd.hours : endH).toLocaleString(undefined, {
          minimumIntegerDigits: 2,
        }),
        minutes: (endH === this.minTimeEnd.hours && endM < this.minTimeEnd.minutes
          ? this.minTimeEnd.minutes
          : endM
        ).toLocaleString(undefined, { minimumIntegerDigits: 2 }),
      },
    );
  }
}

@Component({
  selector: 'sharry-example-header',
  standalone: true,
  imports: [IconComponent, MatButtonModule],
  template: `
    <div style="display: flex; align-items: center; padding-bottom: 0.5rem; font-family: 'HKGrotesk';">
      <button
        mat-icon-button
        class="sharry-small"
        (click)="previousClicked('year')">
        <sharry-icon
          name="caret-double-left"
          type="phosphor"></sharry-icon>
      </button>
      <button
        mat-icon-button
        class="sharry-small"
        (click)="previousClicked('month')">
        <sharry-icon
          name="caret-left"
          type="phosphor"></sharry-icon>
      </button>
      <span style="flex: 1; height: 1em; font-weight: 500; text-align: center;">{{ periodLabel }}</span>
      <button
        mat-icon-button
        class="sharry-small"
        (click)="nextClicked('month')">
        <sharry-icon
          name="caret-right"
          type="phosphor"></sharry-icon>
      </button>
      <button
        mat-icon-button
        class="sharry-small"
        (click)="nextClicked('year')">
        <sharry-icon
          name="caret-double-right"
          type="phosphor"></sharry-icon>
      </button>
    </div>
  `,
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ExampleHeaderComponent<D> implements OnDestroy {
  private _destroyed = new Subject<void>();

  constructor(
    private _calendar: MatCalendar<D>,
    private _dateAdapter: DateAdapter<D>,
    @Inject(MAT_DATE_FORMATS) private _dateFormats: MatDateFormats,
    cdr: ChangeDetectorRef,
  ) {
    _calendar.stateChanges.pipe(takeUntil(this._destroyed)).subscribe(() => cdr.markForCheck());
  }

  get periodLabel() {
    return this._dateAdapter
      .format(this._calendar.activeDate, this._dateFormats.display.monthYearLabel)
      .toLocaleUpperCase();
  }

  ngOnDestroy() {
    this._destroyed.next();
    this._destroyed.complete();
  }

  previousClicked(mode: 'month' | 'year') {
    this._calendar.activeDate =
      mode === 'month'
        ? this._dateAdapter.addCalendarMonths(this._calendar.activeDate, -1)
        : this._dateAdapter.addCalendarYears(this._calendar.activeDate, -1);
  }

  nextClicked(mode: 'month' | 'year') {
    this._calendar.activeDate =
      mode === 'month'
        ? this._dateAdapter.addCalendarMonths(this._calendar.activeDate, 1)
        : this._dateAdapter.addCalendarYears(this._calendar.activeDate, 1);
  }
}
